import { useMediaQuery } from '@components/Common/DesktopOnly';
import CloseIcon from '@mui/icons-material/Close';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import { Badge, Box, Button, Chip, Drawer, IconButton } from '@mui/material';
import React, { useEffect, useState } from 'react';
import './index.scss';

/** One applied filter, shown as a removable chip. */
export interface AppliedFilter {
  key: string;
  /** Reads as "Industry: Retail". */
  label: string;
  onClear: () => void;
}

interface FilterBarProps {
  /** The filter controls. Rendered inline on a desktop, in the sheet on a phone. */
  children: React.ReactNode;
  /** What is currently applied — drives the count and the chips. */
  applied: AppliedFilter[];
  onApply: () => void;
  onClearAll: () => void;
  /**
   * Called when the sheet is dismissed without applying, so the caller can put
   * the draft back. Without it, half-typed changes leak into the next open.
   */
  onCancel?: () => void;
  /**
   * On desktop, let the reader collapse the inline filter row behind a "Filters" toggle, the way
   * the phone layout already does. Off by default: most list pages want their filters in view. The
   * dashboard opts in, because its filters sit above several screens of charts the reader scrolls
   * past far more often than they re-filter.
   */
  collapsibleOnDesktop?: boolean;
  /** Where to remember the desktop open/closed choice. Omit to not persist it. */
  storageKey?: string;
}

/**
 * A filter row that collapses on a phone.
 *
 * On a desktop the controls sit inline, which is right: there is room, and
 * changing a filter is one click. On a phone that same row was five stacked
 * fields and two buttons — around 420px of chrome above the data, permanently
 * expanded, for something most visits never touch.
 *
 * So below 768px it becomes one line: a Filters button carrying the active
 * count, and a chip per applied filter so you can see and undo what is on
 * without opening anything. The controls move into a bottom sheet.
 *
 * This mirrors what the CRM lists already do with SearchFilter, rather than
 * being a third way to filter.
 */
const FilterBar: React.FC<FilterBarProps> = ({
  children,
  applied,
  onApply,
  onClearAll,
  onCancel,
  collapsibleOnDesktop = false,
  storageKey,
}) => {
  const isPhone = !useMediaQuery('(min-width: 768px)');
  const [open, setOpen] = useState(false);
  // Desktop collapse. Expanded by default; the stored choice is read once on mount.
  const [expanded, setExpanded] = useState(true);

  // Never leave the sheet open behind a resize into the desktop layout.
  useEffect(() => {
    if (!isPhone) setOpen(false);
  }, [isPhone]);

  useEffect(() => {
    if (!collapsibleOnDesktop || !storageKey) return;
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved !== null) setExpanded(saved === '1');
    } catch {
      // Storage can be blocked; the default (expanded) is a fine fallback.
    }
  }, [collapsibleOnDesktop, storageKey]);

  const toggleExpanded = () =>
    setExpanded((prev) => {
      const next = !prev;
      try {
        if (storageKey) localStorage.setItem(storageKey, next ? '1' : '0');
      } catch {
        // Non-fatal: the toggle still works for this session.
      }
      return next;
    });

  if (!isPhone) {
    /*
     * The fields and the buttons share one wrapping box, and the icon sits outside it. They used
     * to be siblings of the icon in a single wrapping row, so when the row ran out of width the
     * buttons wrapped to the container's padding edge -- 32px to the LEFT of the first field,
     * because the icon and its gap only indent the first line.
     */
    const desktopBody = (
      <Box className="cc-filterbar__body">
        {children}
        {/*
          * Apply and Clear travel together: grouped and always rendered, Clear disabled rather
          * than absent when there is nothing to clear, so the pair keeps one shape and one line.
          */}
        <Box className="cc-filterbar__actions">
          <Button size="small" variant="contained" onClick={onApply}>Apply</Button>
          <Button
            size="small"
            variant="text"
            onClick={onClearAll}
            disabled={applied.length === 0}
          >
            Clear
          </Button>
        </Box>
      </Box>
    );

    if (collapsibleOnDesktop) {
      return (
        <Box className="cc-filterbar cc-filterbar--inline cc-filterbar--collapsible">
          <Box className="cc-filterbar__deskhead">
            <Badge
              badgeContent={applied.length}
              color="primary"
              overlap="rectangular"
              className="cc-filterbar__badge"
            >
              <Button
                size="small"
                variant="outlined"
                startIcon={<FilterAltOutlinedIcon />}
                endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                onClick={toggleExpanded}
                aria-expanded={expanded}
              >
                Filters
              </Button>
            </Badge>
            {/* Collapsed, the applied filters stay visible and undoable, like the phone layout. */}
            {!expanded &&
              applied.map((f) => (
                <Chip
                  key={f.key}
                  label={f.label}
                  size="small"
                  onDelete={f.onClear}
                  className="cc-filterbar__chip"
                />
              ))}
          </Box>
          {expanded && desktopBody}
        </Box>
      );
    }

    return (
      <Box className="cc-filterbar cc-filterbar--inline">
        <FilterAltOutlinedIcon className="cc-filterbar__icon" fontSize="small" />
        {desktopBody}
      </Box>
    );
  }

  const dismiss = () => {
    onCancel?.();
    setOpen(false);
  };

  return (
    <>
      <Box className="cc-filterbar cc-filterbar--compact">
        <Badge
          badgeContent={applied.length}
          color="primary"
          overlap="rectangular"
          className="cc-filterbar__badge"
        >
          <Button
            size="small"
            variant="outlined"
            startIcon={<FilterAltOutlinedIcon />}
            onClick={() => setOpen(true)}
          >
            Filters
          </Button>
        </Badge>

        {/* The applied filters, undoable without opening the sheet. */}
        {applied.map((f) => (
          <Chip
            key={f.key}
            label={f.label}
            size="small"
            onDelete={f.onClear}
            className="cc-filterbar__chip"
          />
        ))}
      </Box>

      <Drawer
        anchor="bottom"
        open={open}
        onClose={dismiss}
        slotProps={{ paper: { className: 'cc-filterbar__sheet' } }}
      >
        <Box className="cc-filterbar__sheet-head">
          <span className="cc-filterbar__sheet-title">Filters</span>
          <IconButton aria-label="Close filters" onClick={dismiss}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box className="cc-filterbar__sheet-body">{children}</Box>

        <Box className="cc-filterbar__sheet-foot">
          <Button
            variant="text"
            onClick={() => { onClearAll(); setOpen(false); }}
            disabled={applied.length === 0}
          >
            Clear all
          </Button>
          <Button
            variant="contained"
            onClick={() => { onApply(); setOpen(false); }}
          >
            Apply
          </Button>
        </Box>
      </Drawer>
    </>
  );
};

export default FilterBar;
